import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import { Box, Button, Flex, Input, Link, Menu, Spinner, Text, Tooltip } from '@lego/klik-ui';
import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import type {
  DetailSectionProps,
  EditableFieldEditorProps,
} from '@/components/common/details/DetailsBox';
import { useWorkspaceOwners } from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';
import { adPortalURL } from '@/utility/external_urls';
import { CopyableLink } from '../common/CopyableLink';
import { ApplicationIdSelect } from './ApplicationIdSelect';

const SEARCH_DEBOUNCE_MS = 300;

type OwnerEntry = {
  objectId: string;
  name: string;
  ownerId: string;
};

export const buildWorkspaceDetailsSections = (
  workspace: WorkspaceResponseV2,
  isLoading = false
): DetailSectionProps[] => {
  const workspaceBeatsDashboardURL = `${import.meta.env.VITE_ELASTIC_URL}/app/dashboards#/view/7d51bf97-e4b3-46d4-acc6-7aa73b6ad36e?_g=(filters:!(('$state':(store:globalState),meta:(key:metadata.workspaceId,params:(query:${workspace.id}),type:phrase),query:(match_phrase:(metadata.workspaceId:${workspace.id})))),refreshInterval:(pause:!t,value:60000),time:(from:now-15m,to:now))`;
  const workspaceOTelDashboardURL = `${import.meta.env.VITE_ELASTIC_URL}/app/dashboards#/view/b2c3d4e5-f6a7-8901-bcde-f12345678901?_g=(filters:!(('$state':(store:globalState),meta:(key:metadata.workspaceId,params:(query:${workspace.id}),type:phrase),query:(match_phrase:(metadata.workspaceId:${workspace.id})))),refreshInterval:(pause:!t,value:60000),time:(from:now-15m,to:now))`;

  return [
    {
      items: {
        'Workspace ID': { body: workspace.id },
        Description: { body: workspace.description || '-' },
        Created: { body: new Date(workspace.createdAt).toLocaleString(dateTimeFormat.local) },
        Observability: {
          body: (
            <Flex align="center" direction="row" gap="2">
              <Tooltip label="Devices running core v14 and below">
                <Link href={workspaceBeatsDashboardURL} isExternal padding={0}>
                  Beats
                </Link>
              </Tooltip>
              <Box as="span" color="gray.400">
                |
              </Box>
              <Tooltip label="Devices running core v15 and above">
                <Link href={workspaceOTelDashboardURL} isExternal padding={0}>
                  OTel
                </Link>
              </Tooltip>
            </Flex>
          ),
        },
        Owner: {
          body: (
            <Link
              href={`https://omnisight.thelegogroup.com/architecture-gateway/products/${workspace.enterpriseArchitectureProductId}/overview`}
              isExternal
              overflow="hidden"
              padding={0}
            >
              Architecture Gateway
            </Link>
          ),
        },
        'Write AD Group': {
          body: workspace.adGroupUUID ? (
            <Box width="220px">
              <CopyableLink
                url={`${adPortalURL}/${workspace.adGroupUUID ?? ''}`}
                value={workspace.adGroupUUID}
              />
            </Box>
          ) : (
            'Missing ID'
          ),
        },
        'Read AD Group': {
          body: workspace.readonlyAdGroupUUID ? (
            <Box width="220px">
              <CopyableLink
                url={`${adPortalURL}/${workspace.readonlyAdGroupUUID ?? ''}`}
                value={workspace.readonlyAdGroupUUID}
              />
            </Box>
          ) : (
            'Missing ID'
          ),
        },
        'Operator AD Group': {
          body: workspace.operatorAdGroupUUID ? (
            <Box width="220px">
              <CopyableLink
                url={`${adPortalURL}/${workspace.operatorAdGroupUUID ?? ''}`}
                value={workspace.operatorAdGroupUUID}
              />
            </Box>
          ) : (
            'Missing ID'
          ),
        },
        'Product ID': { body: workspace.enterpriseArchitectureProductId || 'Missing ID' },
        'Application IDs': {
          body: <ApplicationIdSelect isLoading={isLoading} workspace={workspace} />,
        },
        'Vulnerability email notifications': buildSecurityScanNotificationUnsubscriptionsItem(
          workspace.securityScanNotificationUnsubscriptions,
          workspace.id,
          !!workspace.adGroupUUID
        ),
      },
    },
  ];
};

const buildSecurityScanNotificationUnsubscriptionsItem = (
  unsubscriptions: string[],
  workspaceId: number,
  hasAdGroup: boolean
) => {
  const uniqueUnsubscriptions = Array.from(new Set(unsubscriptions));

  return {
    body: (
      <SecurityScanNotificationUnsubscriptions
        count={uniqueUnsubscriptions.length}
        hasAdGroup={hasAdGroup}
      />
    ),
    editableValue: uniqueUnsubscriptions.join('\n'),
    renderEditor: (props: EditableFieldEditorProps) => (
      <SecurityScanNotificationUnsubscriptionsEditor {...props} workspaceId={workspaceId} />
    ),
  };
};

const SecurityScanNotificationUnsubscriptions = ({
  count,
  hasAdGroup,
}: {
  count: number;
  hasAdGroup: boolean;
}) => {
  if (!hasAdGroup) {
    return (
      <Text color="slate.500" fontSize="sm">
        No AD group configured for this workspace.
      </Text>
    );
  }

  if (count === 0) {
    return (
      <Text color="slate.500" fontSize="sm">
        All AD-group owners subscribed
      </Text>
    );
  }

  return (
    <Text fontSize="sm">
      {count} {count === 1 ? 'owner' : 'owners'} excluded
    </Text>
  );
};

type OwnerOptionListProps = {
  owners: OwnerEntry[];
  selectedObjectIds: string[];
  onToggle: (objectId: string) => void;
};

const OwnerOptionList = ({ owners, selectedObjectIds, onToggle }: OwnerOptionListProps) => (
  <Box minH="52px">
    {owners.map((owner) => (
      <Flex as="label" cursor="pointer" justifyContent="space-between" key={owner.objectId} p={2}>
        <Box overflow="hidden">
          <Text fontSize="sm" isTruncated>
            {owner.name}
          </Text>
          <Text color="slate.500" fontSize="xs" isTruncated>
            {owner.ownerId}
          </Text>
        </Box>
        <input
          aria-label={`Toggle ${owner.name}`}
          checked={selectedObjectIds.includes(owner.objectId)}
          onChange={() => onToggle(owner.objectId)}
          type="checkbox"
        />
      </Flex>
    ))}
  </Box>
);

type OwnerSearchResultsProps = {
  isLoading: boolean;
  error?: Error;
  owners: OwnerEntry[];
  selectedObjectIds: string[];
  onToggle: (objectId: string) => void;
};

const OwnerSearchResults = ({
  isLoading,
  error,
  owners,
  selectedObjectIds,
  onToggle,
}: OwnerSearchResultsProps) => {
  if (isLoading) {
    return (
      <Flex alignItems="center" gap={2} p={2}>
        <Spinner size="sm" variant="sm" />
        <Text fontSize="sm">Loading owners...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Text color="red.500" fontSize="sm" p={2}>
        Could not load workspace owners.
      </Text>
    );
  }

  if (owners.length === 0) {
    return (
      <Text color="slate.500" fontSize="sm" p={2}>
        No matching owners found.
      </Text>
    );
  }

  return (
    <OwnerOptionList onToggle={onToggle} owners={owners} selectedObjectIds={selectedObjectIds} />
  );
};

const SecurityScanNotificationUnsubscriptionsEditor = ({
  value,
  onChange,
  workspaceId,
}: EditableFieldEditorProps & { workspaceId?: number }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query, SEARCH_DEBOUNCE_MS);
  const selectedObjectIds = useMemo(
    () =>
      value
        .split(/[\n,;]+/)
        .map((id) => id.trim().toLowerCase())
        .filter(Boolean),
    [value]
  );
  const { data: ownersResponse, error, isLoading } = useWorkspaceOwners(workspaceId);

  const allOwners: OwnerEntry[] = useMemo(
    () => (ownersResponse?.owners as OwnerEntry[] | undefined) ?? [],
    [ownersResponse]
  );

  const updateSelection = (objectIds: string[]) => onChange(objectIds.join('\n'));

  const handleToggle = (objectId: string) => {
    if (selectedObjectIds.includes(objectId)) {
      updateSelection(selectedObjectIds.filter((id) => id !== objectId));
      return;
    }
    updateSelection([...selectedObjectIds, objectId]);
  };

  const selectedOwners = useMemo(
    () => allOwners.filter((o) => selectedObjectIds.includes(o.objectId)),
    [allOwners, selectedObjectIds]
  );

  const availableOwners = useMemo(() => {
    const available = allOwners.filter((o) => !selectedObjectIds.includes(o.objectId));
    const trimmed = debouncedQuery.trim().toLowerCase();
    if (!trimmed) return available;
    return available.filter(
      (o) => o.name.toLowerCase().includes(trimmed) || o.ownerId.toLowerCase().includes(trimmed)
    );
  }, [allOwners, selectedObjectIds, debouncedQuery]);

  return (
    <Box>
      <Menu closeOnSelect={false} placement="bottom-start">
        <Menu.Button as={Button} size="sm" variant="outline">
          {selectedObjectIds.length === 0
            ? '0 users excluded'
            : `${selectedObjectIds.length} ${
                selectedObjectIds.length === 1 ? 'user' : 'users'
              } excluded`}
        </Menu.Button>
        <Menu.List maxH="320px" maxW="340px" minW="280px" overflowY="auto" p={2}>
          <Input
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter owners"
            size="sm"
            value={query}
          />
          <Text color="slate.500" fontSize="xs" mt={1}>
            Select owners to exclude from vulnerability emails.
          </Text>
          {selectedObjectIds.length > 0 && (
            <Button mt={2} onClick={() => updateSelection([])} size="xs" variant="ghost">
              Clear exclusions
            </Button>
          )}
          {selectedOwners.length > 0 && (
            <Box mt={3}>
              <Text color="slate.600" fontSize="xs" fontWeight="medium" px={2}>
                Excluded users
              </Text>
              <OwnerOptionList
                onToggle={handleToggle}
                owners={selectedOwners}
                selectedObjectIds={selectedObjectIds}
              />
            </Box>
          )}
          <Box mt={2}>
            <OwnerSearchResults
              error={error}
              isLoading={isLoading}
              onToggle={handleToggle}
              owners={availableOwners}
              selectedObjectIds={selectedObjectIds}
            />
          </Box>
        </Menu.List>
      </Menu>
    </Box>
  );
};
